import { html, css } from 'lit-element';
import { FormElement } from './form-element-base';
import { HeaderStyles } from '../styles/header-styles';
import '@trumedianetworks/tmn-core-components/src/tmn-radio';
import '@trumedianetworks/tmn-core-components/src/tmn-input';
import '@trumedianetworks/tmn-core-components/src/tmn-checkbox';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import '@trumedianetworks/tmn-core-components/src/tmn-select';
import '../search/league-search';

export const LIMIT_TYPE = {
    NONE: 'NONE', // meaning all available
    ALLOW: 'ALLOW',
    DENY: 'DENY',
};

const limitOptions = [
    {
        name: LIMIT_TYPE.NONE,
        displayKey: 'Allow All Leagues',
    },
    {
        name: LIMIT_TYPE.ALLOW,
        displayKey: 'Allow Selected Leagues',
    },
];
// Stats perform leagues are very similar across soccer/cricket so we abstract most here
export class FormElementSPLeagues extends FormElement {
    constructor() {
        super();
    }

    static get styles() {
        return [
            super.styles,
            HeaderStyles.styles,
            css`
                :host {
                    padding: 0 10px;
                    border-radius: 8px;
                    background-color: #f9f9f9;
                    display: flex;
                    flex-direction: column;
                }
                tmn-select {
                    font-size: 14px;
                    min-width: 205px;
                }
                tmn-radio-group {
                    display: flex;
                    flex-flow: column;
                }
                tmn-radio {
                    display: flex;
                }
                .radio-container {
                    display: flex;
                    flex-flow: column;
                    border-radius: 8px;
                    padding: 6px;
                    margin-bottom: 2px;
                    margin-right: 4px;
                    background-color: var(--app-surface-color);
                }
                .leagues-container {
                    padding: 6px 12px;
                }
                league-search {
                    padding: 6px 4px;
                    height: 30px;
                }
                tmn-button {
                    margin: 8px;
                }
                .deny-mode {
                    border-left: solid 4px #cb6015;
                }
                .header-container {
                    display: flex;
                    align-items: center;
                }
            `,
        ];
    }

    static get properties() {
        return {
            value: {
                type: Object,
            },
            authOptions: {
                type: Array,
            },
            availableLeagues: {
                type: Array,
            },
        };
    }

    firstUpdated() {
        this.addEventListener('league-detail-changed', this.handleLeagueDetailChanged);
        this.addEventListener('league-detail-removed', this.handleLeagueRemoved);
    }

    render() {
        if (this.isViewMode()) {
            return html`${this.renderHeader()}
                <div class="content">${this.renderDisplayView()}</div>`;
        }
        return html`
            ${this.renderEditHeader()}
            <div class="content">${this.renderEditView()}</div>
        `;
    }

    renderEditHeader() {
        return html`<div class="header-container">
            <h3>League Options</h3>
            ${this.renderManageLeaguesChooser()}
        </div>`;
    }

    renderHeader() {
        return html`<h3>League Options</h3>`;
    }

    renderDisplayView() {
        const { limitType } = this.getSelectedValues();
        const isAllSelected = limitType === LIMIT_TYPE.NONE;
        if (isAllSelected) {
            return html`<b>Allow All Leagues</b>`;
        }
        return html`<div>${this.renderLeagueList()}</div>`;
    }

    renderEditView() {
        const { limitType } = this.getSelectedValues();
        const isAllSelected = limitType === LIMIT_TYPE.NONE;
        const isAllowSelected = limitType === LIMIT_TYPE.ALLOW;
        return html`<tmn-radio-group>
                <div class="radio-container">
                    <tmn-radio
                        @click=${this.handleLeagueOptionChanged}
                        name="limitType"
                        value=${LIMIT_TYPE.NONE}
                        .checked=${isAllSelected}
                        >Allow All Leagues</tmn-radio
                    >
                </div>
                <div class="radio-container">
                    <tmn-radio
                        @click=${this.handleLeagueOptionChanged}
                        name="limitType"
                        value=${LIMIT_TYPE.ALLOW}
                        .checked=${isAllowSelected}
                        >Allow Selected Leagues</tmn-radio
                    >
                </div>
            </tmn-radio-group>

            ${isAllowSelected ? this.renderLeagueList() : null}`;
    }

    renderEditView() {
        const { limitType } = this.getSelectedValues();
        const isAllowSelected = limitType === LIMIT_TYPE.ALLOW;

        return html`${isAllowSelected ? this.renderLeagueList() : null}`;
    }

    renderManageLeaguesChooser() {
        const { limitType } = this.getSelectedValues();
        const selection = limitOptions.filter((s) => s.name === limitType);

        return html`<tmn-select
            class="limit-select"
            .placeholder=${'Select Mode'}
            @tmn-selections-changed=${this.handleManageLeaguesChanged}
            .selections=${selection}
            .data=${limitOptions}
            nosearch></tmn-select>`;
    }

    renderLeagueList() {
        const selectedValues = this.getSelectedValues();
        const { selectedLeaguesData } = selectedValues;
        const viewMode = this.isViewMode();
        return html`<div class="leagues-container">
            ${viewMode ? null : this.renderSelectLeague()}
            ${selectedLeaguesData.map((leagueDetail) => {
                return this.renderLeagueDetail(leagueDetail, selectedValues);
            })}
            ${viewMode
                ? null
                : html`<tmn-button @click=${this.handleClearLeagues}>
                      Clear All Leagues
                  </tmn-button>`}
        </div>`;
    }
    renderSelectLeague() {
        const { value, availableLeagues = [] } = this;
        const { leagues = [] } = value ?? {};
        const remainingLeagues = availableLeagues.filter(
            (al) => !leagues.some((l) => l.leagueId === al.name)
        );
        return html`<league-search
            .data=${remainingLeagues}
            @leagues-selected=${this.handleAddSelectedLeagues}
            .placeholderText=${`Select Leagues`}></league-search>`;
    }
    // override
    renderLeagueDetail(leagueDetail) {}

    // override
    getSelectedValues() {}

    preventPropagation(e) {
        // stop radio group listening to every click
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
    }

    handleClearLeagues(e) {
        e.stopPropagation();
        const { path, value } = this;
        const newValue = {
            ...value,
            leagues: [],
        };
        this.actions.setStagedEditByPath(path, newValue);
    }

    handleLeagueOptionChanged(e) {
        this.preventPropagation(e);
        const { path, value } = this;
        const composedEventPath = e.composedPath() || [];
        const target = composedEventPath[0];
        const targetValue = target.value;
        const newValue = {
            ...value,
            limitType: targetValue,
        };
        this.actions.setStagedEditByPath(path, newValue);
    }

    handleAddSelectedLeagues(e) {
        const { path, value } = this;
        const { detail } = e;
        const { selections } = detail;
        const { selectedLeaguesData } = this.getSelectedValues();
        const newSelections = [
            ...selections.map((sel) => ({
                displayKey: sel.displayKey,
                availableSeasons: sel.seasons,
                leagueId: sel.name,
                limitType: LIMIT_TYPE.ALLOW,
                // select first 2 seasons by default (which should be 2 most recent)
                seasons: sel.seasons?.filter((s, idx) => idx < 2).map((s) => s.name),
            })),
            ...selectedLeaguesData,
        ];
        // note target.name needs to match the expected properties for COMP_DATA
        const newValue = {
            ...value,
            leagues: newSelections,
        };
        this.actions.setStagedEditByPath(path, newValue);
    }

    handleLeagueDetailChanged(e) {
        const { path, value } = this;
        const { detail, target } = e;
        const { leagueId, seasons, seasonLimitType = LIMIT_TYPE.NONE } = detail;
        const { leagues = [] } = value;
        const newLeagues = leagues.map((lg) =>
            lg.leagueId === leagueId ? { leagueId, seasons, limitType: seasonLimitType } : lg
        );
        const newValue = {
            ...value,
            leagues: newLeagues,
        };

        this.actions.setStagedEditByPath(path, newValue);
    }

    handleSeasonAuthChanged(e) {
        const { path, value } = this;
        const { detail } = e;
        const { leagueId, ...seasonAuths } = detail;
        const newValue = {
            ...value,
            ...seasonAuths,
        };
        this.actions.setStagedEditByPath(path, newValue);
    }

    handleLeagueRemoved(e) {
        const { path, value } = this;
        const { detail, target } = e;
        const { leagueId } = detail;
        const { leagues = [] } = value;
        const newLeagues = leagues.filter((lg) => lg.leagueId !== leagueId);
        const newValue = {
            ...value,
            leagues: newLeagues,
        };

        this.actions.setStagedEditByPath(path, newValue);
    }

    handleManageLeaguesChanged(e) {
        const { path, value } = this;
        const { detail } = e;
        const { selections } = detail;
        // note target.name needs to match the expected properties for COMP_DATA
        const newValue = {
            ...value,
            limitType: selections[0].name,
        };
        this.actions.setStagedEditByPath(path, newValue);
    }
}
